import { ITeamResponseSchema } from '@monetize/types/app';
import {
  MAvatar,
  MFlex,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { createColumnHelper } from '../../../../../components/Monetize/DataTableNew/appTable';
import { actionCatalogColumn } from '../../../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  textBodyTemplate,
} from '../../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { TEAM_TYPE_DISPLAY } from '../../../../../constants/teams';
import { TeamMembersCell } from './TeamMembersCell';

const columnHelper = createColumnHelper<ITeamResponseSchema>();

export type TeamListColumnContext = ColumnContextBase<ITeamResponseSchema>;

/**
 * Sort/filter flags mirror the backend whitelist for GET /api/teams:
 * filterable = name; sortable = name, createDate, modifyDate. `type`, `owner`
 * and `users` are display-only (unknown params return HTTP 400).
 */
export const TEAM_LIST_CATALOG: ColumnCatalog<
  ITeamResponseSchema,
  TeamListColumnContext
> = [
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Name', 260),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'type',
    label: 'Type',
    build: () =>
      columnHelper.accessor('type', {
        ...getColHeaderWithSize('Type', 120),
        cell: enumDisplayBodyTemplate({ displayMap: TEAM_TYPE_DISPLAY }),
        enableSorting: false,
      }),
  },
  {
    id: 'owner',
    label: 'Owner',
    build: () =>
      columnHelper.accessor('owner', {
        ...getColHeaderWithSize('Owner', 220),
        cell: ({ row, getValue }) => {
          const ownerId = getValue();
          const ownerName =
            row.original.users.find(({ id }) => id === ownerId)?.name ??
            ownerId;
          return (
            <MFlex alignItems="center" gap={2} minW={0}>
              <MAvatar name={ownerName} size="xs" />
              {/* Truncate at the pixel edge rather than the last word break:
                  these are usually emails, which otherwise break at their `+`
                  or `-` and waste most of the column. */}
              <MTooltip label={ownerName} placement="bottom-start">
                <MText truncate>{ownerName}</MText>
              </MTooltip>
            </MFlex>
          );
        },
        enableSorting: false,
      }),
  },
  {
    id: 'users',
    label: 'Members',
    build: () =>
      columnHelper.accessor('users', {
        ...getColHeaderWithSize('Members', 320),
        cell: ({ row }) => (
          <TeamMembersCell
            users={row.original.users}
            owner={row.original.owner}
          />
        ),
        enableSorting: false,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 180),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 180),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<ITeamResponseSchema>(),
];
